<script setup>
import { ref, watch } from 'vue';

import { can } from '@/@layouts/plugins/casl';
import { getComputedNavLinkToProp, isNavGroupActive } from '@/@layouts/utils';
import { useNavItemVisibility } from '@/composables/useNavItemVisibility';
import { useAccountStore } from '@/stores/account.js';

// One level of the mobile menu. A group renders another MobileNavItems for its children,
// so the Amazon -> PPC -> links nesting (and any deeper one) needs no extra code. Each
// level owns the open/closed state of its own groups.
const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  depth: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['navigate']);

defineOptions({ name: 'MobileNavItems' });

const account = useAccountStore();
const { currentBusinessProfile } = storeToRefs(account);
const { isEnvAllowed, isChannelAllowed } = useNavItemVisibility();

const route = useRoute();
const router = useRouter();

const canAccessItem = (item) => {
  if (!item.action || !item.subject) return true;
  const isAllowed = can(item.action, item.subject);
  if (item.isNeedBusinessProfile) return isAllowed && !!currentBusinessProfile.value;
  return isAllowed;
};

// Env, CASL and channel gates, recursing into sub-groups so a group with no visible
// descendant is dropped instead of rendering an empty header.
const isItemVisible = (item) => {
  if (item.disable || !canAccessItem(item) || !isEnvAllowed(item) || !isChannelAllowed(item)) return false;
  if (item.children) return item.children.some(isItemVisible);
  return true;
};

const getActiveGroups = () =>
  props.items
    .filter((item) => item.children && isNavGroupActive(item.children, router))
    .map((item) => item.title);

const openGroups = ref(new Set(getActiveGroups()));

watch(
  () => route.path,
  () => {
    openGroups.value = new Set([...openGroups.value, ...getActiveGroups()]);
  },
);

const toggleGroup = (title) => {
  const next = new Set(openGroups.value);
  if (next.has(title)) {
    next.delete(title);
  } else {
    next.add(title);
  }
  openGroups.value = next;
};

const isGroupOpen = (title) => openGroups.value.has(title);
</script>

<template>
  <template
    v-for="item in items"
    :key="item.title"
  >
    <RouterLink
      v-if="item.to && isItemVisible(item)"
      class="mobile-nav-list__link"
      :class="{ 'mobile-nav-list__link--child': depth > 0 }"
      :style="{ '--mobile-nav-depth': depth }"
      v-bind="getComputedNavLinkToProp(item)"
      @click="emit('navigate')"
    >
      <VIcon
        v-if="depth < 2"
        :icon="item.icon?.icon"
        class="mobile-nav-list__icon"
      />
      {{ item.title }}
    </RouterLink>

    <div
      v-else-if="item.children && isItemVisible(item)"
      class="mobile-nav-list__group"
    >
      <button
        class="mobile-nav-list__group-title"
        :class="{ 'mobile-nav-list__group-title--nested': depth > 0 }"
        :style="{ '--mobile-nav-depth': depth }"
        @click="toggleGroup(item.title)"
      >
        <VIcon
          :icon="item.icon?.icon"
          class="mobile-nav-list__icon"
        />
        {{ item.title }}
        <VIcon
          icon="lucide:chevron-down"
          class="mobile-nav-list__chevron"
          :class="{ 'mobile-nav-list__chevron--open': isGroupOpen(item.title) }"
        />
      </button>
      <div
        class="mobile-nav-list__group-children"
        :class="{ 'mobile-nav-list__group-children--open': isGroupOpen(item.title) }"
      >
        <div class="mobile-nav-list__group-children-inner">
          <MobileNavItems
            :items="item.children"
            :depth="depth + 1"
            @navigate="emit('navigate')"
          />
        </div>
      </div>
    </div>
  </template>
</template>

<style lang="scss" scoped>
.mobile-nav-list {
  &__group-title {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 14px 0 14px calc(var(--mobile-nav-depth, 0) * 16px);
    font-size: 1rem;
    font-weight: 500;
    color: var(--wp-text-primary);
    border: none;
    border-bottom: 1px solid var(--wp-border-primary);
    background: transparent;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    cursor: pointer;

    &--nested {
      font-size: 0.95rem;
    }
  }

  &__chevron {
    margin-left: auto;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    transition: transform 0.25s ease;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__group-children {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.25s ease;

    &-inner {
      min-height: 0;
      overflow: hidden;
    }

    &--open {
      grid-template-rows: 1fr;
    }
  }

  &__link {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 0;
    font-size: 1.1rem;
    font-weight: 500;
    text-decoration: none;
    color: var(--wp-text-primary);
    border-bottom: 1px solid var(--wp-border-primary);
    transition: color 0.15s;

    &:hover,
    &.router-link-active {
      color: var(--wp-text-accent);
    }

    &--child {
      padding-left: calc(var(--mobile-nav-depth, 1) * 16px);
      font-size: 1rem;
      font-weight: 400;
    }
  }

  &__icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    opacity: 0.75;
  }
}
</style>
